/**
 * Patient schemas — intake creation, profile edits, demographic fields.
 *
 * Sources: intakeDataService.ts, ClientProfilePage.tsx,
 * PRD_CLIENTS_FHIR_MAPPING.md, fhir-model/resources/patient.ts
 */
import { z } from 'zod';

export const patientIdSchema = z.string().min(1, 'Patient id is required');

/**
 * Intake lead patient creation — minimum fields collected during public intake.
 */
export const intakePatientSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Valid email is required'),
  dateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth must be YYYY-MM-DD')
    .optional(),
  postalCode: z.string().optional(),
});

export type IntakePatientInput = z.infer<typeof intakePatientSchema>;

/**
 * Client profile form state — maps to `ClientProfileFormState` in the profile page.
 * Used for the profile tab save path (Patient update).
 */
export const clientProfileFormSchema = z.object({
  preferredFirstName: z.string().trim().min(1, 'Preferred first name is required'),
  preferredLastName: z.string().trim().min(1, 'Preferred last name is required'),
  legalFirstName: z.string().optional(),
  legalLastName: z.string().optional(),
  dateOfBirth: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().regex(/^[A-Za-z]{2}$/, 'State must be a two-letter abbreviation').optional().or(z.literal('')),
  zipCode: z.string().regex(/^\d{5}(?:-\d{4})?$/, 'ZIP code must be 5 digits or ZIP+4').optional().or(z.literal('')),
  phone: z.string().optional(),
  email: z.string().email('Valid email is required').optional().or(z.literal('')),
  genderIdentity: z.string().optional(),
  lgbtqia: z.array(z.string()).optional(),
  religion: z.array(z.string()).optional(),
  raceEthnicity: z.array(z.string()).optional(),
  languages: z.array(z.string()).optional(),
});

export type ClientProfileFormValues = z.infer<typeof clientProfileFormSchema>;

/**
 * Patient contact (emergency contact, guardian, etc.) from ContactsTab.
 */
export const patientContactSchema = z.object({
  name: z.string().trim().min(1, 'Contact name is required'),
  relationship: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  address: z.string().optional(),
});

export type PatientContactInput = z.infer<typeof patientContactSchema>;
